// src/pages/QuotationDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

import QuotationPreview from "../components/QuotationPreview";
import ApprovalFlowHorizontal from "../components/ApprovalFlowHorizontal";
import { apiURL, COMPANY_PREFIX } from "../config/config";
import bankAccounts from "../data/bankAccounts.json";

import ActionButtons from "../components/ActionButtons";
import BasicInfoCard from "../components/BasicInfoCard";
import ItemsTable from "../components/ItemsTable";
import QuotationSummary from "../components/QuotationSummary";
import BackButton from "../components/BackButton";
import SendToCustomerModal from "../components/SendToCustomerModal";
import HandoverSection from "../components/HandoverSection";
import {
  canApprove,
  canReject,
  canCancel,
} from "../utils/buttonVisibility";
import {
  findCachedQuotationById,
  upsertCachedQuotation,
} from "../utils/quotationCache";
import {
  applyApprovalActionToQuotation,
  resetQuotationApprovalState,
} from "../utils/quotationApprovalState";

const getQuotationClient = (quotationData = {}) => {
  if (quotationData.clientId && typeof quotationData.clientId === "object") {
    return quotationData.clientId;
  }

  return quotationData.clientDetails || null;
};

const hasClientSigningData = (client = {}) =>
  Boolean(client?.email) && Array.isArray(client?.authorizedApprovers);

const QuotationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quotation, setQuotation] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasFreshQuotation, setHasFreshQuotation] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sendingToCustomer, setSendingToCustomer] = useState(false);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const { username: email = "", level } = user;

  const updateApprovalState = (status) => {
    const updatedQuotation = applyApprovalActionToQuotation(quotation, {
      level,
      approver: email,
      status,
    });

    setQuotation(updatedQuotation);
    upsertCachedQuotation(updatedQuotation);
  };

  useEffect(() => {
    let isCurrent = true;
    window.scrollTo({ top: 0, behavior: "smooth" });

    const cachedQuotation = findCachedQuotationById(id);
    setQuotation(cachedQuotation || null);
    setClientDetails(null);
    setLoading(!cachedQuotation);
    setHasFreshQuotation(false);
    if (cachedQuotation) {
      setQuotation(cachedQuotation);

      const cachedClient = getQuotationClient(cachedQuotation);
      if (cachedClient) {
        setClientDetails(cachedClient);
      }

      setLoading(false);
    }

    const fetchQuotationDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}quotations/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data;
        if (!isCurrent) return;
        setQuotation(data);
        upsertCachedQuotation(data);
        setHasFreshQuotation(true);

        const quotationClient = getQuotationClient(data);

        if (quotationClient) {
          setClientDetails(quotationClient);
        }

        if (!hasClientSigningData(quotationClient) && data.clientId) {
          const clientId = data.clientId?._id || data.clientId;
          const clientResponse = await axios.get(
            `${apiURL}clients/${clientId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (!isCurrent) return;
          setClientDetails(clientResponse.data);
        }

        setLoading(false);

        if (
          data.approvalStatus === "Pending" &&
          (!data.approvalHierarchy || data.approvalHierarchy.length === 0)
        ) {
          navigate(`/request-approve-flow/${id}`);
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error("Error fetching quotation details:", error);
        if (!cachedQuotation) {
          toast.error("Failed to fetch quotation details");
        }
        setLoading(false);
      }
    };

    fetchQuotationDetails();
    return () => {
      isCurrent = false;
    };
  }, [id, navigate]);

  const handleDownloadPDF = async (currentQuotation, currentClientDetails) => {
    try {
      const company = user.company || "";
      const year = new Date(currentQuotation.documentDate).getFullYear();
      const documentNo = `${COMPANY_PREFIX}(${currentQuotation.type})-${year}-${currentQuotation.runNumber}`;
      const fileName = `${documentNo} - ${currentQuotation.projectName}.pdf`;

      const bankInfo = bankAccounts.companies?.[company] || {};
      const updatedQuotation = {
        ...currentQuotation,
        clientDetails: currentClientDetails,
      };

      const blob = await pdf(
        <QuotationPreview
          quotationData={updatedQuotation}
          bankInfo={bankInfo}
          isDetailedForm={currentQuotation.isDetailedForm}
          isSpecialForm={currentQuotation.isSpecialForm}
          numberOfSpecialPages={currentQuotation.numberOfSpecialPages}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const approveQuotation = async () => {
    if (
      !canApprove(
        quotation.approvalStatus,
        level,
        quotation.approvalHierarchy,
      )
    ) {
      toast.error("ยังไม่ถึงคิวอนุมัติของคุณ");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const approvalId = quotation.approvalHierarchy[0]?._id;
      if (!approvalId) return;

      await axios.patch(
        `${apiURL}approvals/${approvalId}/approvers`,
        { level, approver: email, status: "Approved" },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      updateApprovalState("Approved");
      toast.success("Quotation approved successfully");
    } catch (error) {
      toast.error("ไม่สามารถอนุมัติได้");
    }
  };

  const confirmApprove = async () => {
    const result = await Swal.fire({
      title: `ยืนยันการอนุมัติ QT-(${quotation.type})-${quotation.runNumber}`,
      text: "คุณต้องการอนุมัติใบเสนอราคานี้ใช่หรือไม่?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "อนุมัติเลย",
      cancelButtonText: "กลับ",
    });
    if (result.isConfirmed) approveQuotation();
  };

  const handleUnlock = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${apiURL}quotations/${quotation._id}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const responseQuotation =
        response.data?.data?.quotation ||
        response.data?.quotation ||
        (response.data?.data?._id ? response.data.data : null) ||
        (response.data?._id ? response.data : null);
      const updatedQuotation = responseQuotation?._id
        ? { ...quotation, ...responseQuotation }
        : resetQuotationApprovalState(quotation);

      setQuotation(updatedQuotation);
      upsertCachedQuotation(updatedQuotation);
      toast.success("ปลดล็อกสำเร็จ");
    } catch (error) {
      toast.error("Failed to unlock quotation");
    }
  };

  const handleSendToCustomer = async ({ to, cc, expiresInDays }) => {
    if (!to) {
      toast.error("กรุณาเลือกอีเมลผู้รับ");
      return;
    }

    try {
      setSendingToCustomer(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${apiURL}quotations/${quotation._id}/send-to-customer`,
        {
          to,
          cc,
          expiresInDays,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const updatedApproval = response.data?.data?.customerApproval;

      if (updatedApproval) {
        setQuotation((current) => ({
          ...current,
          customerApproval: updatedApproval,
        }));
      }

      toast.success("ส่งอีเมลให้ลูกค้าเรียบร้อยแล้ว");
      setIsSendModalOpen(false);
    } catch (error) {
      console.error("Error sending quotation to customer:", error);
      toast.error(
        error.response?.data?.message || "ส่งอีเมลให้ลูกค้าไม่สำเร็จ",
      );
    } finally {
      setSendingToCustomer(false);
    }
  };

  const rejectOrCancelQuotation = async (actionType) => {
    const canPerformAction =
      actionType === "reject"
        ? canReject(quotation.approvalStatus, level, quotation.approvalHierarchy)
        : canCancel(quotation.approvalStatus, level, quotation.approvalHierarchy);

    if (!canPerformAction) {
      toast.error("ยังไม่ถึงคิวอนุมัติของคุณ");
      return;
    }

    const { value: reason } = await Swal.fire({
      title:
        actionType === "reject"
          ? `ยืนยัน Reject QT-(${quotation.type})-${quotation.runNumber}`
          : `ยืนยัน Cancel QT-(${quotation.type})-${quotation.runNumber}`,
      input: "textarea",
      inputPlaceholder: "กรอกเหตุผล...",
      showCancelButton: true,
      confirmButtonText: actionType === "reject" ? "Reject" : "Cancel",
      cancelButtonText: "กลับ",
      inputValidator: (value) =>
        !value.trim() ? "กรุณากรอกเหตุผล!" : null,
    });

    if (!reason) return;

    let statusUpdated = false;

    try {
      const token = localStorage.getItem("token");
      const approvalId = quotation.approvalHierarchy[0]?._id;
      if (!approvalId) return;

      const nextStatus = actionType === "reject" ? "Rejected" : "Canceled";

      await axios.patch(
        `${apiURL}approvals/${approvalId}/approvers`,
        {
          level,
          approver: email,
          status: nextStatus,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Show the new flow icon as soon as the status API succeeds.
      updateApprovalState(nextStatus);
      statusUpdated = true;

      await axios.patch(
        `${apiURL}quotations/${quotation._id}/reason`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(
        actionType === "reject"
          ? "Rejected successfully"
          : "Canceled successfully",
      );

      const updatedQuotation = {
        ...applyApprovalActionToQuotation(quotation, {
          level,
          approver: email,
          status: nextStatus,
        }),
        reason,
      };
      setQuotation(updatedQuotation);
      upsertCachedQuotation(updatedQuotation);
    } catch (error) {
      toast.error(
        statusUpdated
          ? "อัปเดตสถานะแล้ว แต่บันทึกเหตุผลไม่สำเร็จ"
          : "อัปเดตสถานะไม่สำเร็จ",
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
        <ClipLoader color="#6366F1" loading={loading} size={60} />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        <p className="text-gray-600 dark:text-gray-300 text-lg">
          Quotation not found.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-5 sm:py-6 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300 min-h-screen">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <BackButton label="ย้อนกลับ" />
        </div>

        <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 leading-snug">
          คุณกำลังดูใบเสนอราคา:
          <span className="ml-1 sm:ml-2 text-indigo-600 dark:text-indigo-400 font-semibold block sm:inline mt-1 sm:mt-0">
            {(() => {
              const year = new Date(quotation.documentDate).getFullYear();
              const run = quotation.runNumber?.toString().padStart(3, "0");
              const prefix = COMPANY_PREFIX;
              return `${prefix}(${quotation.type})-${year}-${run}`;
            })()}
          </span>
        </h2>

        {["Rejected", "Canceled"].includes(quotation.approvalStatus) &&
          quotation.reason && (
            <div className="mt-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-2 rounded border border-red-300 dark:border-red-800 transition-colors duration-300">
              <span className="font-semibold">
                {quotation.approvalStatus === "Rejected"
                  ? "เหตุผลที่ถูกตีกลับ:"
                  : "เหตุผลที่ถูกยกเลิก:"}
              </span>{" "}
              {quotation.reason}
            </div>
          )}
      </div>

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-yellow-400 mb-5 sm:mb-7">
        Quotation Details
      </h1>

      <div className="mb-6 sm:mb-8">
        <ActionButtons
          quotation={quotation}
          client={clientDetails}
          level={level}
          email={email}
          onEdit={() => navigate(`/quotations/edit/${quotation._id}`)}
          onDownload={() => handleDownloadPDF(quotation, clientDetails)}
          onSendToCustomer={() => setIsSendModalOpen(true)}
          onUnlock={handleUnlock}
          onApprove={confirmApprove}
          onReject={rejectOrCancelQuotation}
          onCancel={rejectOrCancelQuotation}
        />
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100 dark:border-gray-700 transition-colors duration-300">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 sm:mb-5 flex items-center gap-2">
          <span className="inline-block w-1.5 h-5 bg-indigo-500 dark:bg-indigo-400 rounded-full"></span>
          Approval Flow
        </h2>
        <ApprovalFlowHorizontal
          approvalHierarchy={quotation.approvalHierarchy || []}
        />
      </div>

      {hasFreshQuotation && quotation._id === id && quotation.approvalStatus !== "Canceled" && (
        <HandoverSection
          key={quotation._id}
          quotation={quotation}
          onQuotationRefresh={(updated) => {
            setQuotation(updated);
            upsertCachedQuotation(updated);
          }}
        />
      )}

      <div className="mb-6 sm:mb-8">
        <BasicInfoCard quotation={quotation} />
      </div>

      <div className="mb-6 sm:mb-8">
        <ItemsTable items={quotation.items} />
      </div>

      <QuotationSummary quotation={quotation} />

      <SendToCustomerModal
        isOpen={isSendModalOpen}
        client={clientDetails}
        quotation={quotation}
        sending={sendingToCustomer}
        onClose={() => setIsSendModalOpen(false)}
        onSubmit={handleSendToCustomer}
      />
    </div>
  );
};

export default QuotationDetails;
